import { Amount } from 'nomst/Stores/Decision/amount';
import { IEmployee } from 'typings/employee';
import { EventTypes, EntityTypes, AmountCode } from 'typings/enums';
import { observable, action, computed, makeObservable } from 'mobx';
import { EligibilityCalculator } from 'nomst/Stores/Utility/eligibilityCalculator';
import { currencyConversion } from 'utils/MobxDataManager/singletons';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { IEligibilityProrationOverrideStore } from 'typings/eligibilityProrationOverrideStore';
import { dataManagerFactory, getEmployees } from 'utils/MobxDataManager/singletons';
import { StockAmount } from 'nomst/Stores/Decision/stockAmount';
import { Employee } from 'nomst/Stores/Decision/employee';

export class EligibilityProrationOverrideStore implements
    IEligibilityProrationOverrideStore {
    _shouldShowDialogBox: boolean;
    _employeeAmounts: Amount[];
    _stockAmount: StockAmount;
    _configuration: Configuration;

    @observable
    _isSaving: boolean;

    constructor(configuration: Configuration) {
        makeObservable(this);
        this._configuration = configuration;
    }

    @action
    resetEmployeeAmounts(): void {
        this._employeeAmounts = [];
    }

    @action
    setEmployeeAmounts(employeeAmounts: Amount[]): void {
        this._employeeAmounts = employeeAmounts;
    }

    @action
    setEmployeeStockAmount(stockAmount: StockAmount): void {
        this._stockAmount = stockAmount;
    }

    @computed
    get employeeAmounts(): Amount[] {
        return this._employeeAmounts;
    }

    @computed
    get isSaving(): boolean {
        return this._isSaving;
    }

    getEligibilityOverrideEventValue(value: boolean): Nullable<boolean> {
        return value ? null : false;
    }

    getOverrideEligibilityEvent(
        employee: IEmployee,
        employeeAmount: Amount | StockAmount,
        initialIsEligible: boolean): IEmployeeEvent {
        const eventData = {
            amountCycleId: employeeAmount.amountCycleId,
            amountCode: employeeAmount.amountCode,
            originalValue: this.getEligibilityOverrideEventValue(initialIsEligible),
            value: this.getEligibilityOverrideEventValue(employeeAmount.isEligible)
        };

        return {
            employeeSourceId: employee.employeeSourceId,
            useEmployeeTopic: false,
            eventType: EventTypes.OverrideAmountEligibilityEvent,
            entityType: EntityTypes.EmployeeCycle,
            userId: this._configuration.userId,
            data: eventData,
            metadata: undefined
        } as IEmployeeEvent;
    }

    getOverrideProrationEvent(
        employee: IEmployee,
        employeeAmount: Amount | StockAmount,
        initialIsProratedOverride: boolean): IEmployeeEvent {
        const eventData = {
            amountCycleId: employeeAmount.amountCycleId,
            amountCode: employeeAmount.amountCode,
            originalValue: initialIsProratedOverride,
            value: employeeAmount.isProrationOverride
        };

        return {
            employeeSourceId: employee.employeeSourceId,
            useEmployeeTopic: false,
            eventType: EventTypes.OverrideAmountProrationEvent,
            entityType: EntityTypes.EmployeeCycle,
            userId: this._configuration.userId,
            data: eventData,
            metadata: undefined
        } as IEmployeeEvent;
    }

    getResetAmountEvent(
        employee: IEmployee,
        amountCycleId: number,
        amountCode: string,
        originalValue: number): IEmployeeEvent {
        const eventData = {
            amountCycleId,
            amountCode,
            originalValue,
            value: 0
        };

        return {
            employeeSourceId: employee.employeeSourceId,
            useEmployeeTopic: false,
            eventType: EventTypes.UpdateAmountEvent,
            entityType: EntityTypes.EmployeeCycle,
            userId: this._configuration.userId,
            data: eventData,
            metadata: {
                affectWorkflow: false
            }
        } as IEmployeeEvent;
    }

    getUpdateBudgetEvent(
        employee: IEmployee,
        budgetCycleId: Nullable<number>,
        originalBudgetAmount: number,
        newBudgetAmount: number): IEmployeeEvent {
        const eventData = {
            budgetCycleId,
            originalValue: originalBudgetAmount,
            value: newBudgetAmount
        };

        return {
            employeeSourceId: employee.employeeSourceId,
            useEmployeeTopic: false,
            eventType: EventTypes.UpdateBudgetEvent,
            entityType: EntityTypes.EmployeeCycle,
            userId: this._configuration.userId,
            data: eventData,
            metadata: undefined
        } as IEmployeeEvent;
    }

    async getOverrideAmountEligibilityEvents(
        countryCurrency: string,
        employee: IEmployee,
        budgetCycleId: number,
        amounts: Amount[],
        peers: IEmployee[]): Promise<IEmployeeEvent[]>  {
        const events = [] as IEmployeeEvent[];

        const budgetCalculationResult = await new EligibilityCalculator().recalculateEmployeeBudget(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            currencyConversion,
            countryCurrency,
            employee,
            peers,
            budgetCycleId,
            amounts,
            this._configuration.cycleBudgets);

        if (!budgetCalculationResult) {
            return events;
        }

        for (const amount of amounts) {
            if (budgetCalculationResult.shouldResetAmount) {
                events.push(this.getResetAmountEvent(
                    employee,
                    amount.amountCycleId,
                    amount.amountCode,
                    amount.value));

                if (amount.value > 0) {
                    amount.setValue(0);
                }
            }
            events.push(this.getOverrideEligibilityEvent(
                employee,
                amount,
                amount.initialIsEligible));
        }

        if (budgetCalculationResult.newBudget !== undefined) {
            events.push(this.getUpdateBudgetEvent(
                employee,
                budgetCycleId,
                budgetCalculationResult.originalBudget,
                budgetCalculationResult.newBudget));
        }

        return events;
    }

    @action
    async save(countryCurrency: string, employee: Employee): Promise<void> {
        this._isSaving = true;
        if (employee.amounts) {
            await this.saveEmployeeAmounts(countryCurrency, employee);
        }
        if (employee.stockAmount) {
            await this.saveEmployeeStockAmount(employee);
        }

        this._isSaving = false;
    }

    async saveEmployeeStockAmount(employee: Employee): Promise<void> {
        const events = [] as IEmployeeEvent[];
        const employeeAmount = employee.stockAmount;
        events.push(this.getOverrideEligibilityEvent(
            employee,
            employeeAmount,
            employeeAmount.initialIsEligible));

        const overrideAmountProrationEvents = this.getOverrideAmountProrationEvents(
            employee,
            employeeAmount,
            employeeAmount.initialIsProrationOverride);

        events.push(...overrideAmountProrationEvents);
        employeeAmount.resetInitialIsEligible();
        employeeAmount.resetInitialIsProration();

        if (events.length === 0) {
            return;
        }

        await dataManagerFactory.getEventDataManager().sendEvents(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            events);
    }

    async saveEmployeeAmounts(
        countryCurrency: string,
        employee: Employee
    ): Promise<void> {
        const events = [] as IEmployeeEvent[];
        const peers = getEmployees.allEmployees().filter(peer =>
            peer.country === employee.country &&
            peer.managerSourceId === employee.managerSourceId &&
            peer.employeeSourceId !== employee.employeeSourceId);

        if (!employee.amounts) {
            return;
        }

        const eligibleAmounts = employee.amounts
            .filter(amount => amount.initialIsEligible !== amount.isEligible) as Amount[];

        const proratedAmounts = employee.amounts.filter(
            amount => amount.initialIsProrationOverride !== amount.isProrationOverride) as Amount[];

        const allModifiedAmounts = eligibleAmounts.concat(proratedAmounts);

        const budgetCycleIds = allModifiedAmounts
            .map((amount) => amount.budgetCycleId);

        const modifiedBudgetCycleIds = budgetCycleIds
            .filter((item, index) => budgetCycleIds.indexOf(item) === index );

        for (const budgetCycleId of modifiedBudgetCycleIds) {
            const employeeAmountEvents = await this.saveEmployeeAmount(
                countryCurrency,
                employee,
                budgetCycleId ?? 0,
                allModifiedAmounts,
                peers);

            events.push(...employeeAmountEvents);
        }

        if (events.length === 0) {
            return;
        }

        await dataManagerFactory.getEventDataManager().sendEvents(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            events);

        // When marking ineligible for promotion, update UI to show no promotion request
        const promo = eligibleAmounts.find(amount => amount.amountCode === AmountCode.Promotion);
        if (promo && !promo.isEligible && employee.hasPromotionRequest) {
            employee.hasPromotionRequest = false;
        }
    }

    async saveEmployeeAmount(
        countryCurrency: string,
        employee: IEmployee,
        budgetCycleId: number,
        amounts: Amount[],
        peers: IEmployee[]): Promise<IEmployeeEvent[]> {
        const events = [] as IEmployeeEvent[];

        const overrideAmountEligibilityEvents = await this.getOverrideAmountEligibilityEvents(
            countryCurrency,
            employee,
            budgetCycleId,
            amounts,
            peers);

        events.push(...overrideAmountEligibilityEvents);
        for (const amount of amounts) {
            const overrideAmountProrationEvents = this.getOverrideAmountProrationEvents(
                employee,
                amount,
                amount.initialIsProrationOverride);

            events.push(...overrideAmountProrationEvents);
            amount.resetInitialIsEligible();
            amount.resetInitialIsProration();
        }
        return events;
    }

    getOverrideAmountProrationEvents(
        employee: IEmployee,
        employeeAmount: Amount | StockAmount,
        initialIsProratedOverride: boolean): IEmployeeEvent[] {
        const events = [] as IEmployeeEvent[];
        if (employeeAmount.isProrationOverride === initialIsProratedOverride) {
            return events;
        }

        events.push(this.getOverrideProrationEvent(
            employee,
            employeeAmount,
            initialIsProratedOverride));

        return events;
    }
}

export default { EligibilityProrationOverrideStore };